import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// ─── SLIDES DATA ───────────────────────────────────────────────
const SLIDES = [
  {
    id: 1,
    badgeColor: '#4bb543',
    badgeLabel: 'The Human Shipping Network',
    title: 'Le monde bouge.\nFlitship relie.',
    quote: '"Votre colis voyage avec quelqu\'un qui rentre chez lui."',
    illustBg: '#eef2fd',
    illustIcon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <path d="M8 32 Q24 8 40 32" stroke="#3c6df0" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
        <circle cx="8" cy="32" r="4" fill="#3c6df0"/>
        <circle cx="40" cy="32" r="4" fill="#f38a3e"/>
        <g transform="translate(24, 16) rotate(12)">
          <path d="M-6 0 L-3 -1.5 L0 -4 L1.5 -1.5 L6 0 L1.5 1.5 L0 4 L-3 1.5 Z" fill="#3c6df0"/>
        </g>
      </svg>
    ),
    slideTitle: 'Un réseau humain mondial',
    slideSub: 'Des millions de personnes vivent entre plusieurs territoires.',
    statNum: '97%',
    statLabel: 'missions livrées',
    statColor: '#3c6df0',
  },
  {
    id: 2,
    badgeColor: '#f38a3e',
    badgeLabel: 'Paiement sécurisé',
    title: 'Zéro risque.\nArgent bloqué.',
    quote: '"Ni avant, ni autrement.\nUniquement à la livraison."',
    illustBg: '#fff4e6',
    illustIcon: (
      <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
        <rect x="8" y="14" width="32" height="20" rx="3" stroke="#f38a3e" strokeWidth="2.5" fill="none"/>
        <path d="M8 22h32" stroke="#f38a3e" strokeWidth="2.5"/>
        <circle cx="32" cy="28" r="3" fill="#f38a3e"/>
      </svg>
    ),
    slideTitle: 'Escrow Stripe garanti',
    slideSub: 'Paiement bloqué jusqu\'à confirmation de remise.',
    statNum: 'Stripe',
    statLabel: 'infrastructure de paiement sécurisée',
    statColor: '#f38a3e',
  },
  {
    id: 3,
    badgeColor: '#4bb543',
    badgeLabel: 'Trust Protocol',
    title: 'La confiance,\nstructurée.',
    quote: '"La confiance ne doit pas dépendre du hasard.\nElle peut être structurée."',
    illustBg: '#e8f5e8',
    illustIcon: null,
    slideTitle: null,
    checks: [
      { title: 'Identité vérifiée', sub: 'Chaque membre est identifié' },
      { title: 'Paiement séquestre', sub: 'Libéré à la livraison confirmée' },
      { title: 'Code de remise unique', sub: 'Confirmation par les deux parties' },
    ],
    statNum: 'Trust',
    statLabel: 'Protocol · confiance cumulative',
    statColor: '#4bb543',
  },
  {
    id: 4,
    badgeColor: '#3c6df0',
    badgeLabel: 'Rejoindre le réseau',
    title: 'Comment voulez-\nvous commencer ?',
    quote: '"Chaque trajet peut devenir utile. Chaque colis porte quelque chose qui compte."',
    isCta: true,
  },
];

// ─── COMPONENT ────────────────────────────────────────────────
export function WelcomeOnboarding({ onComplete }) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const navigate = useNavigate();

  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };
  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance && currentSlide < SLIDES.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
    if (distance < -minSwipeDistance && currentSlide > 0) {
      setCurrentSlide(prev => prev - 1);
    }
  };

  const handleSkip = () => {
    localStorage.setItem('flitship_onboarded', 'true');
    onComplete();
  };

  const handleNext = () => {
    if (currentSlide < SLIDES.length - 1) {
      setCurrentSlide(prev => prev + 1);
    }
  };

  const handleCta = (path) => {
    localStorage.setItem('flitship_onboarded', 'true');
    onComplete();
    navigate(path);
  };

  const slide = SLIDES[currentSlide];

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-[#1f1f1f]"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >

      {/* DARK PANEL */}
      <div className="flex-shrink-0 px-6 pt-14 pb-6" style={{ minHeight: '220px', maxHeight: '52vh' }}>

        {/* Skip */}
        <div className="flex justify-end mb-6">
          <button
            onClick={handleSkip}
            className="font-['Inter'] text-[13px] text-white/50 hover:text-white/80 transition-colors"
          >
            Passer
          </button>
        </div>

        {/* Badge + Title */}
        <div className="space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/[0.08]">
            <span
              className="w-2 h-2 rounded-full"
              style={{ backgroundColor: slide.badgeColor }}
            />
            <span className="font-['Space_Grotesk'] text-[12px] font-medium text-white/80">
              {slide.badgeLabel}
            </span>
          </div>

          <h1 className="font-['Space_Grotesk'] text-[32px] font-bold text-white leading-[1.15] whitespace-pre-line">
            {slide.title}
          </h1>
        </div>

        {/* Quote */}
        <p className="mt-5 font-['Georgia'] text-[14px] italic text-white/40 leading-relaxed">
          {slide.quote.split('\n').map((line, i) => (
            <span key={i}>{line}{i === 0 && <br/>}</span>
          ))}
        </p>

      </div>

      {/* LIGHT PANEL */}
      <div className="flex-1 bg-[#faf6f2] rounded-t-[24px] px-6 pt-6 pb-[calc(80px+env(safe-area-inset-bottom,0px))] flex flex-col overflow-hidden">

        {/* SLIDES 1-3 content */}
        {!slide.isCta && (
          <div className="flex-1 flex flex-col justify-center">

            {/* Illustration + title */}
            {slide.slideTitle && (
              <div className="flex items-center gap-4 mb-6">
                <div
                  className="w-[72px] h-[72px] rounded-[16px] flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: slide.illustBg }}
                >
                  {slide.illustIcon}
                </div>
                <div className="flex-1">
                  <p className="font-['Space_Grotesk'] text-[15px] font-semibold text-[#1f1f1f] mb-0.5">
                    {slide.slideTitle}
                  </p>
                  <p className="font-['Inter'] text-[13px] text-[#737373] leading-snug">
                    {slide.slideSub}
                  </p>
                </div>
              </div>
            )}

            {/* Checklist slide 3 */}
            {slide.checks && (
              <div className="space-y-3 mb-6">
                {slide.checks.map((c, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-5 h-5 rounded-full bg-[#e8f5e8] flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M2 5l2 2 4-4" stroke="#4bb543" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <p className="font-['Space_Grotesk'] text-[14px] font-semibold text-[#1f1f1f]">{c.title}</p>
                      <p className="font-['Inter'] text-[12px] text-[#737373]">{c.sub}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Stat pill */}
            <div className="flex items-center gap-2 bg-white border border-[#e8e3dc] rounded-full px-4 py-2 w-fit">
              <span
                className="font-['Space_Grotesk'] text-[16px] font-bold"
                style={{ color: slide.statColor }}
              >
                {slide.statNum}
              </span>
              <span className="font-['Inter'] text-[12px] text-[#737373]">
                {slide.statLabel}
              </span>
            </div>
          </div>
        )}

        {/* SLIDE 4 — CTA */}
        {slide.isCta && (
          <div className="flex-1 flex flex-col justify-center">
            <div className="grid grid-cols-2 gap-3 mb-6">
              {/* Envoyer */}
              <button
                onClick={() => handleCta('/send-package')}
                className="bg-white border border-[#e8e3dc] rounded-[12px] p-4 text-center active:scale-[0.97] transition-transform"
              >
                <div className="w-[32px] h-[32px] bg-[#eef2fd] rounded-[9px] flex items-center justify-center mx-auto mb-2">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M2.5 15.5L8 10M2.5 2.5L15.5 8L8 10L5.5 15.5L2.5 2.5Z" stroke="#3c6df0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <p className="font-['Space_Grotesk'] text-[14px] font-semibold text-[#1f1f1f] leading-tight">
                  Envoyer<br/>un colis
                </p>
                <p className="font-['Inter'] text-[11px] text-[#737373] mt-1">
                  Je cherche un voyageur
                </p>
              </button>
              {/* Transporter */}
              <button
                onClick={() => handleCta('/publish-trip/route')}
                className="bg-white border border-[#e8e3dc] rounded-[12px] p-4 text-center active:scale-[0.97] transition-transform"
              >
                <div className="w-[32px] h-[32px] bg-[#fff4e6] rounded-[9px] flex items-center justify-center mx-auto mb-2">
                  <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                    <path d="M14.5 3.5L9.5 14.5L7.5 9.5L2.5 7.5L14.5 3.5Z" stroke="#f38a3e" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M7.5 9.5L14.5 3.5" stroke="#f38a3e" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <p className="font-['Space_Grotesk'] text-[14px] font-semibold text-[#1f1f1f] leading-tight">
                  Publier<br/>un trajet
                </p>
                <p className="font-['Inter'] text-[11px] text-[#737373] mt-1">
                  Je voyage et transporte
                </p>
              </button>
            </div>
            <button
              onClick={handleSkip}
              className="w-full h-[42px] bg-transparent border border-[#e8e3dc] rounded-[12px] font-['Inter'] text-[13px] font-medium text-[#1f1f1f] hover:bg-[#f0ebe4] transition-colors"
            >
              Explorer d'abord
            </button>
            <p className="font-['Space_Grotesk'] text-[11px] font-medium text-[#a3a3a3] text-center tracking-wide uppercase">
              The Human Shipping Network
            </p>
          </div>
        )}

        {/* DOTS + BUTTON */}
        <div className="mt-auto pt-4 flex items-center justify-between">
          {/* Progress dots */}
          <div className="flex items-center gap-1.5">
            {SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrentSlide(i)}
                className={`h-[4px] rounded-full transition-all duration-300 ${
                  i === currentSlide ? 'w-[18px] bg-[#3c6df0]' : 'w-[4px] bg-[#e8e3dc]'
                }`}
              />
            ))}
          </div>

          {/* Next button — outline style */}
          {!slide.isCta && (
            <button
              onClick={handleNext}
              className="flex items-center gap-2 h-[44px] px-5 border border-[#3c6df0] rounded-[12px] font-['Space_Grotesk'] text-[14px] font-semibold text-[#3c6df0] hover:bg-[#3c6df0]/5 transition-colors"
            >
              Suivant
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M6 4l4 4-4 4" stroke="#3c6df0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── HOOK — inchangé ──────────────────────────────────────────
export function useWelcomeOnboarding() {
  const [shouldShow, setShouldShow] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);

  React.useEffect(() => {
    const hasOnboarded = localStorage.getItem('flitship_onboarded') === 'true';
    setIsLoading(false);
    setShouldShow(!hasOnboarded);
  }, []);

  const markComplete = () => {
    localStorage.setItem('flitship_onboarded', 'true');
    setShouldShow(false);
  };

  return { shouldShow, isLoading, markComplete };
}

export default WelcomeOnboarding;
